import React, { useState } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ExpandMore,
  Search,
  Send,
  CheckCircle,
  Help,
  ContactSupport
} from '@mui/icons-material';
import SectionHeader from '@shared/SectionHeader';
import { useAuth } from '@contexts/AuthContext';

interface FAQItem {
  id: string;
  category: string;
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    id: 'faq1',
    category: 'Programări',
    question: 'Cum pot programa o consultație?',
    answer: 'Poți programa o consultație accesând secțiunea "Vizite & Programări" din meniul principal, apoi selectează clinica dorită și alege un interval disponibil.'
  },
  {
    id: 'faq2',
    category: 'Programări',
    question: 'Pot anula sau modifica o programare?',
    answer: 'Da, poți anula sau modifica programarea din secțiunea "Vizite & Programări". Pentru modificări cu mai puțin de 24h înainte, te rugăm să contactezi clinica direct.'
  },
  {
    id: 'faq3',
    category: 'Cont',
    question: 'Cum îmi actualizez datele personale?',
    answer: 'Accesează "Profilul meu" din meniu, apoi secțiunea "Date personale" unde poți edita informațiile tale.'
  },
  {
    id: 'faq4',
    category: 'Animale',
    question: 'Cum adaug un animal nou?',
    answer: 'Mergi la "Animalele mele" și apasă butonul "Adaugă animal". Completează datele necesare și salvează.'
  },
  {
    id: 'faq5',
    category: 'Documente',
    question: 'Unde găsesc rezultatele analizelor?',
    answer: 'Toate rezultatele analizelor și documentele medicale sunt disponibile în secțiunea "Analize & Documente".'
  },
  {
    id: 'faq6',
    category: 'Plăți',
    question: 'Ce metode de plată acceptați?',
    answer: 'Acceptăm plăți cu cardul bancar, transfer bancar și numerar la clinică. Plățile online sunt procesate în siguranță.'
  }
];

const HelpPage: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expanded, setExpanded] = useState<string | false>(false);
  
  // Support ticket form
  const [ticketCategory, setTicketCategory] = useState('');
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketError, setTicketError] = useState<string | null>(null);
  const [ticketSuccess, setTicketSuccess] = useState(false);

  const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const categories = ['all', ...Array.from(new Set(faqData.map(faq => faq.category)))];

  const filteredFAQs = faqData.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSubmitTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!ticketCategory || !ticketSubject || !ticketMessage) {
      setTicketError('Te rugăm să completezi toate câmpurile.');
      return;
    }

    setTicketLoading(true);
    setTicketError(null);

    try {
      // TODO: Replace with actual API call
      console.log('Submitting ticket:', {
        category: ticketCategory,
        subject: ticketSubject,
        message: ticketMessage,
        userId: user?.id
      });

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      setTicketSuccess(true);
      setTicketCategory('');
      setTicketSubject('');
      setTicketMessage('');

      setTimeout(() => {
        setTicketSuccess(false);
      }, 5000);
    } catch (err) {
      console.error('Error submitting ticket:', err);
      setTicketError('Eroare la trimiterea mesajului. Te rugăm să încerci din nou.');
    } finally {
      setTicketLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <SectionHeader
        title="Ajutor & Suport"
        subtitle="Găsește răspunsuri la întrebările tale sau contactează echipa de suport"
      />

      {/* FAQ Section */}
      <Box mt={3} mb={5}>
        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Help color="primary" sx={{ fontSize: 32 }} />
              <Typography variant="h5" fontWeight={600}>
                Întrebări frecvente (FAQ)
              </Typography>
            </Box>

            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
              <TextField
                placeholder="Caută în FAQ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )
                }}
                sx={{ flex: 1, minWidth: 250 }}
              />
              
              <Box display="flex" gap={1} flexWrap="wrap">
                {categories.map(cat => (
                  <Chip
                    key={cat}
                    label={cat === 'all' ? 'Toate' : cat}
                    onClick={() => setSelectedCategory(cat)}
                    color={selectedCategory === cat ? 'primary' : 'default'}
                    variant={selectedCategory === cat ? 'filled' : 'outlined'}
                    sx={{ borderRadius: 2 }}
                  />
                ))}
              </Box>
            </Box>

            {filteredFAQs.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  Nu am găsit rezultate pentru căutarea ta.
                </Typography>
              </Box>
            ) : (
              <Box>
                {filteredFAQs.map((faq) => (
                  <Accordion
                    key={faq.id}
                    expanded={expanded === faq.id}
                    onChange={handleAccordionChange(faq.id)}
                    sx={{ 
                      mb: 1,
                      borderRadius: 2,
                      '&:before': { display: 'none' },
                      boxShadow: 'none',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Chip label={faq.category} size="small" color="primary" variant="outlined" />
                        <Typography fontWeight={expanded === faq.id ? 600 : 500}>
                          {faq.question}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography color="text.secondary">
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Contact Support Section */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <ContactSupport color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h5" fontWeight={600}>
              Contactează suportul
            </Typography>
          </Box>

          {ticketSuccess && (
            <Alert 
              severity="success" 
              icon={<CheckCircle />}
              sx={{ mb: 3 }}
              onClose={() => setTicketSuccess(false)}
            >
              Mesajul tău a fost trimis cu succes! Echipa noastră îți va răspunde în cel mai scurt timp posibil.
            </Alert>
          )}

          {ticketError && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => setTicketError(null)}
            >
              {ticketError}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Nu ai găsit răspunsul la întrebarea ta? Trimite-ne un mesaj și te vom contacta în cel mai scurt timp.
          </Typography>

          <Box component="form" onSubmit={handleSubmitTicket}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Categorie</InputLabel>
              <Select
                value={ticketCategory}
                onChange={(e) => setTicketCategory(e.target.value)}
                label="Categorie"
                disabled={ticketLoading}
              >
                <MenuItem value="programari">Programări</MenuItem>
                <MenuItem value="cont">Cont & Profil</MenuItem>
                <MenuItem value="plati">Plăți & Facturi</MenuItem>
                <MenuItem value="tehnic">Problemă tehnică</MenuItem>
                <MenuItem value="altele">Altele</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Subiect"
              value={ticketSubject}
              onChange={(e) => setTicketSubject(e.target.value)}
              disabled={ticketLoading}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Mesaj"
              multiline
              rows={6}
              value={ticketMessage}
              onChange={(e) => setTicketMessage(e.target.value)}
              disabled={ticketLoading}
              placeholder="Descrie în detaliu problema sau întrebarea ta..."
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={ticketLoading ? <CircularProgress size={20} /> : <Send />}
              disabled={ticketLoading || !ticketCategory || !ticketSubject || !ticketMessage}
              sx={{ textTransform: 'none', borderRadius: 2, minWidth: 150 }}
            >
              {ticketLoading ? 'Se trimite...' : 'Trimite mesaj'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default HelpPage;

